import { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

export default function RewardSettings() {
    const [settings, setSettings] = useState({
        article_reward_amount: '1.00',
        article_reward_currency: 'EUR',
        min_words_count: '300',
        auto_approve: 'false'
    });
    const [originalSettings, setOriginalSettings] = useState({});
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [settingsRes, historyRes] = await Promise.all([
                api.get('/admin/rewards/settings'),
                api.get('/admin/rewards/history?limit=10')
            ]);

            const loadedSettings = settingsRes.data.settings;
            setSettings(loadedSettings);
            setOriginalSettings(loadedSettings);
            setHistory(historyRes.data.history);
        } catch (error) {
            console.error('Erreur chargement:', error);
            setMessage({ type: 'error', text: 'Erreur lors du chargement des paramètres' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 'true' : 'false') : value
        }));
        setMessage({ type: '', text: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            // Validation
            const amount = parseFloat(settings.article_reward_amount);
            if (isNaN(amount) || amount < 0) {
                setMessage({ type: 'error', text: 'Montant de récompense invalide' });
                setSaving(false);
                return;
            }

            const minWords = parseInt(settings.min_words_count);
            if (isNaN(minWords) || minWords < 100) {
                setMessage({ type: 'error', text: 'Nombre minimum de mots invalide (min: 100)' });
                setSaving(false);
                return;
            }

            await api.put('/admin/rewards/settings', settings);
            
            setOriginalSettings(settings);
            setMessage({ type: 'success', text: 'Paramètres enregistrés avec succès' });
            console.log('✅ Paramètres de récompense mis à jour');

            // Recharger l'historique
            const historyRes = await api.get('/admin/rewards/history?limit=10');
            setHistory(historyRes.data.history);
        } catch (error) {
            console.error('❌ Erreur sauvegarde:', error);
            setMessage({ 
                type: 'error', 
                text: error.response?.data?.error || 'Erreur lors de la sauvegarde' 
            });
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-yellow-400 mb-4"></i>
                    <p className="text-text-secondary">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <AdminLayout>
            <div className="p-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Paramètres des récompenses</h1>
                    <p className="text-gray-400">Configurez les règles de récompense pour les contributeurs</p>
                </div>

                <div className="space-y-6">
            <div className="bg-surface rounded-lg shadow-lg border border-gray-700/30 p-6">
                {message.text && (
                    <div className={`mb-6 p-4 rounded-lg border ${
                        message.type === 'success' 
                            ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                            : 'bg-red-500/10 border-red-500/30 text-red-500'
                    }`}>
                        <div className="flex items-center">
                            <i className={`fas ${
                                message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
                            } mr-2`}></i>
                            <p>{message.text}</p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Montant récompense */}
                    <div>
                        <label htmlFor="article_reward_amount" className="block text-sm font-medium text-text mb-2">
                            <i className="fas fa-euro-sign mr-2 text-yellow-400"></i>
                            Montant de la récompense par article
                        </label>
                        <div className="flex items-center space-x-3">
                            <input
                                type="number"
                                id="article_reward_amount"
                                name="article_reward_amount"
                                min="0"
                                step="0.01"
                                value={settings.article_reward_amount}
                                onChange={handleChange}
                                className="flex-1 px-4 py-3 bg-background border border-gray-700 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                required
                            />
                            <span className="text-text-secondary font-medium">€</span>
                        </div>
                        <p className="mt-2 text-sm text-text-secondary">
                            Crédit ajouté automatiquement au compte WHMCS du contributeur après approbation de l'article
                        </p>
                    </div>

                    {/* Devise */}
                    <div>
                        <label htmlFor="article_reward_currency" className="block text-sm font-medium text-text mb-2">
                            <i className="fas fa-money-bill-wave mr-2 text-yellow-400"></i>
                            Devise
                        </label>
                        <select
                            id="article_reward_currency"
                            name="article_reward_currency"
                            value={settings.article_reward_currency}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        >
                            <option value="EUR">EUR (€)</option>
                            <option value="USD">USD ($)</option>
                            <option value="GBP">GBP (£)</option>
                        </select>
                    </div>

                    {/* Nombre minimum de mots */}
                    <div>
                        <label htmlFor="min_words_count" className="block text-sm font-medium text-text mb-2">
                            <i className="fas fa-align-left mr-2 text-yellow-400"></i>
                            Nombre minimum de mots requis
                        </label>
                        <input
                            type="number"
                            id="min_words_count"
                            name="min_words_count"
                            min="100"
                            step="50"
                            value={settings.min_words_count}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-yellow-400"
                            required
                        />
                        <p className="mt-2 text-sm text-text-secondary">
                            Les articles doivent contenir au moins ce nombre de mots pour être soumis
                        </p>
                    </div>

                    {/* Approbation automatique */}
                    <div className="bg-yellow-400/5 border border-yellow-400/20 rounded-lg p-4">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="auto_approve"
                                checked={settings.auto_approve === 'true'}
                                onChange={handleChange}
                                className="w-5 h-5 text-yellow-400 bg-background border-gray-700 rounded focus:ring-2 focus:ring-yellow-400"
                            />
                            <span className="ml-3 text-text font-medium">
                                <i className="fas fa-bolt mr-2 text-yellow-400"></i>
                                Approbation automatique des articles
                            </span>
                        </label>
                        <p className="mt-2 ml-8 text-sm text-text-secondary">
                            ⚠️ Si activé, tous les articles soumis seront automatiquement approuvés et publiés sans validation manuelle. 
                            Non recommandé pour garantir la qualité du contenu.
                        </p>
                    </div>

                    {/* Submit */}
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-700/30">
                        <button
                            type="button"
                            onClick={() => {
                                setSettings(originalSettings);
                                setMessage({ type: '', text: '' });
                            }}
                            disabled={!hasChanges}
                            className="px-6 py-3 bg-gray-700 text-text rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i className="fas fa-undo mr-2"></i>
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !hasChanges}
                            className="px-6 py-3 bg-yellow-400 text-gray-900 font-semibold rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save mr-2"></i>
                                    Enregistrer les paramètres
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Historique des récompenses */}
            <div className="bg-surface rounded-lg shadow-lg border border-gray-700/30 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-700/30">
                    <h3 className="text-xl font-semibold text-text">
                        <i className="fas fa-history mr-2 text-yellow-400"></i>
                        Historique des récompenses récentes
                    </h3>
                </div>

                <div className="overflow-x-auto">
                    {history.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fas fa-inbox text-6xl text-gray-600 mb-4"></i>
                            <p className="text-text-secondary">Aucune récompense distribuée pour le moment</p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-700/30">
                            <thead className="bg-background">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Contributeur
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Article
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Montant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Statut
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                        Date
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {history.map((reward) => (
                                    <tr key={reward.id} className="hover:bg-background/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="text-text font-medium">{reward.username}</div>
                                            <div className="text-xs text-text-secondary">{reward.email}</div>
                                        </td>
                                        <td className="px-6 py-4 text-text">
                                            {reward.article_title || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-yellow-400 font-semibold">
                                                {reward.amount} {reward.currency}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {reward.status === 'credited' ? (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium border bg-green-500/20 text-green-400 border-green-500/30">
                                                    <i className="fas fa-check-circle mr-1"></i>
                                                    Crédité
                                                </span>
                                            ) : reward.status === 'pending' ? (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                                    <i className="fas fa-clock mr-1"></i>
                                                    En cours
                                                </span>
                                            ) : (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium border bg-red-500/20 text-red-400 border-red-500/30">
                                                    <i className="fas fa-times-circle mr-1"></i>
                                                    Échec
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-text-secondary">
                                            {new Date(reward.created_at).toLocaleDateString('fr-FR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Info Panel */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-blue-400 mb-3">
                    <i className="fas fa-info-circle mr-2"></i>
                    Comment fonctionne le système de récompenses ?
                </h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                    <li className="flex items-start">
                        <i className="fas fa-check text-blue-400 mr-2 mt-1"></i>
                        <span>Les contributeurs soumettent leurs articles via leur espace dédié</span>
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-blue-400 mr-2 mt-1"></i>
                        <span>Les admins/rédacteurs valident ou rejettent les soumissions</span>
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-blue-400 mr-2 mt-1"></i>
                        <span>Dès qu'un article est approuvé, le crédit est automatiquement ajouté au compte WHMCS</span>
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-blue-400 mr-2 mt-1"></i>
                        <span>Le contributeur peut utiliser ses crédits pour ses services SunServ</span>
                    </li>
                    <li className="flex items-start">
                        <i className="fas fa-check text-blue-400 mr-2 mt-1"></i>
                        <span>L'historique complet est disponible pour audit et suivi</span>
                    </li>
                </ul>
            </div>
            </div> {/* Fin space-y-6 */}
            </div> {/* Fin p-8 */}
        </AdminLayout>
    );
}
